import { Button } from '@revdotcom/vaporwave';

import styles from '../../styles';

interface SocialLoginProps {
    /**
     * Appropriately prefixed internal social login provider name, e.g. google-abcdef
     */
    socialLogins: string[];

    /**
     * OIDC flow return URL
     */
    returnUrl: string;

    /**
     * Optional username to be passed to social login idp as a hint, if we collected one
     */
    username: string | undefined;
}

/**
 * Renders buttons for social logins or nothing.
 * @param {object} props properties
 * @returns {Element} links element
 */
export const SocialLogins = (props: SocialLoginProps) => {
    const { socialLogins } = props;

    if (!socialLogins || socialLogins.length == 0) return <></>;

    return (
        <>
            <Divider />
            <div sx={styles.socialLoginButtons}>
                {socialLogins.map((sl) => (
                    <SocialButton key={sl} socialLogin={sl} {...props} />
                ))}
            </div>
        </>
    );
};

// ---- OR ----
const Divider = () => (
    <div
        sx={{
            mt: 3,
            position: 'relative',
            textAlign: 'center',
        }}
    >
        <hr
            sx={{
                position: 'absolute',
                top: '1px',
                left: 0,
                width: '100%',
                borderColor: 'gray-80',
                borderWidth: '1px',
                borderBottom: 'none',
                display: 'block',
            }}
        />
        <span
            sx={{
                position: 'relative',
                backgroundColor: 'white',
                px: 3,
                fontSize: 2,
            }}
        >
            OR
        </span>
    </div>
);

// dispatches button rendering to appropriate sub-component
const SocialButton = (props: SocialLoginProps & { socialLogin: string }) => {
    const { socialLogin, returnUrl, username } = props;

    // build the url
    const queryString = new URLSearchParams(
        username ? { returnUrl, username } : { returnUrl },
    ).toString();
    const href = `/login/external/start/${socialLogin}?${queryString}`;

    if (socialLogin.startsWith('google')) return <GoogleButton href={href} />;
    if (socialLogin.startsWith('apple')) return <AppleButton href={href} />;

    return <></>;
};

// google button/link
const GoogleButton = (props: { href: string }) => {
    const { href } = props;

    return (
        <Button
            sizeVariant="small"
            variant="outlined"
            sx={{
                mt: 3,
                display: 'block',
                position: 'relative',
                textAlign: 'center',
                fontSize: 1,
                py: [2, 3],
                variant: 'cards.identity.login.socialLoginButton',
            }}
            color="gray-80"
            href={href}
        >
            <div
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <div
                    sx={{
                        width: '18px',
                        height: '18px',
                        marginRight: '1rem',
                    }}
                >
                    <svg
                        id="google-logo"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 48 48"
                        sx={{
                            display: 'block',
                        }}
                    >
                        <path
                            fill="#EA4335"
                            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                        ></path>
                        <path
                            fill="#4285F4"
                            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                        ></path>
                        <path
                            fill="#FBBC05"
                            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                        ></path>
                        <path
                            fill="#34A853"
                            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                        ></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                    </svg>
                </div>
                <div>Sign in with Google</div>
            </div>
        </Button>
    );
};

// apple button/link
const AppleButton = (props: { href: string }) => {
    const { href } = props;

    return (
        <Button
            sizeVariant="small"
            variant="outlined"
            sx={{
                mt: 3,
                display: 'block',
                position: 'relative',
                textAlign: 'center',
                fontSize: 1,
                py: [2, 3],
                variant: 'cards.identity.login.socialLoginButton',
            }}
            color="gray-80"
            href={href}
        >
            <div
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <div
                    sx={{
                        width: '18px',
                        height: '18px',
                        transform: 'translateY(-60%)',
                        marginRight: '1rem',
                    }}
                >
                    <svg
                        id="apple-logo"
                        viewBox="0 0 20 44"
                        preserveAspectRatio="xMinYMin slice"
                        width="20px"
                        height="44px"
                        x="0px"
                        y="0px"
                    >
                        <g id="Global_Nav_Large_Icons">
                            <path
                                id="Apple"
                                fill="#000000"
                                d="M18.1,24.227c-0.106,0.307-0.217,0.6-0.335,0.881c-0.288,0.664-0.628,1.275-1.022,1.836
                                c-0.536,0.766-0.976,1.296-1.315,1.59c-0.525,0.483-1.088,0.731-1.691,0.744c-0.433,0-0.954-0.123-1.562-0.372
                                c-0.609-0.249-1.169-0.372-1.681-0.372c-0.537,0-1.113,0.123-1.729,0.372c-0.617,0.249-1.114,0.38-1.494,0.392
                                c-0.578,0.025-1.154-0.229-1.729-0.764c-0.367-0.32-0.826-0.869-1.376-1.646c-0.59-0.83-1.075-1.793-1.455-2.891
                                C2.304,22.812,2.1,21.664,2.1,20.553c0-1.273,0.275-2.371,0.826-3.291c0.433-0.739,1.009-1.322,1.73-1.75
                                c0.721-0.428,1.5-0.646,2.339-0.66c0.459,0,1.061,0.142,1.809,0.421c0.746,0.28,1.225,0.422,1.435,0.422
                                c0.157,0,0.689-0.166,1.591-0.497c0.853-0.307,1.573-0.434,2.163-0.384c1.598,0.129,2.798,0.759,3.597,1.894
                                c-1.429,0.866-2.136,2.079-2.122,3.635c0.013,1.212,0.452,2.221,1.316,3.022C17.176,23.736,17.614,24.023,18.1,24.227z
                                M14.08,10.305c0.013,0.127,0.019,0.254,0.019,0.38c0,0.95-0.347,1.837-1.038,2.658c-0.835,0.976-1.845,1.54-2.94,1.451
                                c-0.014-0.114-0.022-0.234-0.022-0.36c0-0.912,0.397-1.888,1.102-2.686c0.352-0.404,0.8-0.74,1.342-1.008
                                C13.085,10.476,13.598,10.33,14.08,10.305z"
                            />
                        </g>
                    </svg>
                </div>
                <div>Sign in with Apple</div>
            </div>
        </Button>
    );
};
